import { PlusOutlined } from "@ant-design/icons";
import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import Processes from "entities/Processes";
import { FieldArray, Formik } from "formik";
import { find, get, isEmpty, keyBy, merge, orderBy } from "lodash";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import DatePicker from "shared/components/Form/DatePicker";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { COLORS } from "shared/style/colors";
import {
  ATTACHMENT_TYPES,
  DYNAMIC_DOCUMENTS,
  INSURANCE_PLAN_PARAMS_RULES,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import QuoteValidityRow from "../../shared/QuoteValidityRow";
import { buildQuoteValidityFormState } from "../../shared/quoteValidity";
import RuleMultiSelectWithExclude from "../../shared/RuleMultiSelectWithExclude";
import { applyExcludeTransformToParams } from "../../shared/subscriptionRulesExclude";
import Documents from "../Blocks/Documents";
import Upsells from "../Blocks/Upsells";
import validation from "./validation";

const RULES_OPTIONS = [
  INSURANCE_PLAN_PARAMS_RULES.IS_PHONE_SIM_OR_ESIM,
  INSURANCE_PLAN_PARAMS_RULES.IS_PHONE_NEW_OR_GRADE_A,
  INSURANCE_PLAN_PARAMS_RULES.IS_PHONE_PURCHASED_INTERNALLY,
  INSURANCE_PLAN_PARAMS_RULES.IS_PHONE_UNDER_THREE_YEARS,
  INSURANCE_PLAN_PARAMS_RULES.IS_PHONE_FOR_PRIVATE_USE,
  INSURANCE_PLAN_PARAMS_RULES.IS_CUSTOMER_IN_FRENCH_POLYNESIA,
];

const SUPPORTING_INFORMATION_OPTIONS = [
  SUPPORTING_INFORMATION_CATEGORIES.DESCRIPTION,
  SUPPORTING_INFORMATION_CATEGORIES.LIMIT,
  SUPPORTING_INFORMATION_CATEGORIES.INCLUDED,
  SUPPORTING_INFORMATION_CATEGORIES.EXCLUDED,
  SUPPORTING_INFORMATION_CATEGORIES.REIMBURSEMENT,
];

const View = ({
  onSubmit,
  cancel,
  data,
  attachments,
  addAttachment,
  unlinkAttachmentFromInsurancePlan,
  offers,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const DOCUMENT_NAME = t("DOCUMENT_NAME", { returnObjects: true });
  const DOCUMENT_NAME_LABEL = t("DOCUMENT_NAME_LABEL", { returnObjects: true });
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const { user: { organization: userOrganization } = {} } =
    useContext(StoreContext);
  const envelop = get(userOrganization, "config.envelop");
  const AVAILABLE_DOCUMENTS = Object.keys(DYNAMIC_DOCUMENTS[envelop] || {});
  const [excludeFlags, setExcludeFlags] = useState({});
  const dynamicAllValuesRef = useRef({});
  const { startDate, endDate, params = [], supportingInformation, ...rest } =
    data;

  const handleSubmitWithExclude = (values) =>
    onSubmit({
      ...values,
      params: applyExcludeTransformToParams(
        values.params,
        excludeFlags,
        dynamicAllValuesRef.current,
      ),
    });

  const eligibilityRules = useMemo(
    () =>
      (params[0]?.rules || []).filter((rule) =>
        RULES_OPTIONS.includes(rule.name),
      ),
    [params],
  );

  const paramsByKey = useMemo(() => {
    const param = params[0] || { id: cuid(), rules: [] };
    const subscriptionRules = (param.rules || []).filter(
      (rule) => !RULES_OPTIONS.includes(rule.name),
    );
    const rulesByKey = keyBy(subscriptionRules, "name");

    const updatedRules = {
      [INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS]: {
        id: cuid(),
        name: "DOCUMENTS",
        value: [],
      },
      [INSURANCE_PLAN_PARAMS_RULES.PROCESSES]: {
        id: cuid(),
        name: "PROCESSES",
        value: [],
      },
    };

    const mergedRules = merge(rulesByKey, updatedRules);

    const storedQuoteValidity = get(
      rulesByKey,
      INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY,
    );
    mergedRules[INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY] = {
      id: get(storedQuoteValidity, "id") || cuid(),
      name: INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY,
      ...buildQuoteValidityFormState(get(storedQuoteValidity, "value")),
    };

    return [{ id: param.id || cuid(), rules: mergedRules }];
  }, [params]);

  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("iconAttachment", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("icon", file.base64);
  };

  return (
    <Formik
      initialValues={{
        ...rest,
        eligibilityRules,
        params: paramsByKey,
        date: [startDate, endDate],
        attachments,
        supportingInformation: orderBy(
          supportingInformation,
          ["category", "weight"],
          ["asc", "desc"],
        ),
      }}
      onSubmit={handleSubmitWithExclude}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({
        isValid,
        handleSubmit,
        setFieldValue,
        setValues,
        values,
        ...formProps
      }) => (
        <div className="grid--1">
          <Header
            key={"header"}
            title={DETAIL.HEADER.TITLE}
            subtitle={DETAIL.HEADER.ACTION}
            actions={[
              {
                text: DETAIL.HEADER.DISCARD,
                onClick: cancel,
                type: "primary--outlined",
                icon: <BiChevronLeft size={20} />,
              },
              {
                text: DETAIL.HEADER.SAVE,
                onClick: handleSubmit,
                type: "primary",
                icon: <BiCheck size={20} />,
              },
            ]}
          />
          <Card key={"generalInfo"} divider={false} title={FORM.GENERAL_INFO}>
            <div className="grid--1">
              <div className="grid--3">
                <div className="d-flex">
                  <span>{FORM.ACTIVE}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.YES}
                    unCheckedChildren={FORM.NO}
                    defaultChecked={get(values, "active")}
                    onChange={(e, _) => setFieldValue("active", e)}
                  />
                </div>
              </div>
              <div className="grid--2">
                <Input
                  placeholder={FORM.NAME}
                  label={FORM.NAME}
                  {...bindInputProps({ name: "name", values, ...formProps })}
                />
                <Input
                  placeholder={FORM.ALIAS}
                  label={FORM.ALIAS}
                  {...bindInputProps({ name: "alias", values, ...formProps })}
                />
                <Input
                  placeholder={FORM.DESCRIPTION}
                  label={FORM.DESCRIPTION}
                  {...bindInputProps({
                    name: "description",
                    values,
                    ...formProps,
                  })}
                />
                <DatePicker
                  placeholder={FORM.DATE}
                  label={FORM.DATE}
                  placeholders={[FORM.START_DATE, FORM.END_DATE]}
                  onChange={(_, dates) => setFieldValue("date", dates)}
                />

                <Input
                  type="file"
                  variant="uploader"
                  listType="picture"
                  showUploadList={false}
                  maxCount={1}
                  onUpload={([file]) => handleFileUpload(file, setFieldValue)}
                  fileList={
                    isEmpty(get(values, "icon", []))
                      ? []
                      : [get(values, "icon")]
                  }
                >
                  <Button type="primary--link">{FORM.ICON}</Button>
                  <div>
                    {get(values, "icon") && (
                      <img
                        className="icon"
                        alt="icon"
                        src={get(values, "icon")}
                      />
                    )}
                  </div>
                </Input>
              </div>
            </div>
          </Card>
          <Card divider={false} title={FORM.CONDITIONS.TITLE} key="rules">
            <div className="m-bottom-16">
              <QuoteValidityRow
                FORM={FORM}
                basePath={`params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}`}
                values={values}
                setFieldValue={setFieldValue}
                {...formProps}
              />
            </div>
            <FieldArray
              name={"eligibilityRules"}
              render={(arrayHelpers) => (
                <div className="grid--1">
                  {get(values, "eligibilityRules", []).map((_, i) => (
                    <div key={_.id} className="d-flex">
                      <div className="flex--grow-1">
                        <Select
                          placeholder={FORM.CHOOSE_RULE}
                          options={RULES_OPTIONS.map((value) => ({
                            label: FORM.ELIGIBILITY_CHECK[value],
                            value,
                          }))}
                          {...bindArrayInputProps({
                            parent: "eligibilityRules",
                            index: i,
                            name: `name`,
                            values,
                            ...formProps,
                          })}
                        />
                      </div>
                      <Input
                        name={`eligibilityRules.${i}.value`}
                        type="switch"
                        defaultChecked={get(values, `eligibilityRules.${i}.value`)}
                        onChange={(e) =>
                          setFieldValue(`eligibilityRules.${i}.value`, e)
                        }
                      />
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                      />
                    </div>
                  ))}
                  <Button
                    type="primary"
                    key="add-row"
                    onClick={() => {
                      arrayHelpers.push({
                        id: cuid(),
                        value: false,
                        name: null,
                      });
                    }}
                  >
                    <PlusOutlined color={COLORS.C_WHITE} />
                  </Button>
                </div>
              )}
            />
          </Card>
          <Card divider={false} title={FORM.CONDITIONS.PROCESSES} key="processes">
            <Row gutter={[16, 16]} align={"middle"}>
              <Col span={24}>
                <Processes
                  NoData={() => <></>}
                  View={({ data }) => (
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={24}>
                        <RuleMultiSelectWithExclude
                          paramIndex={0}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.PROCESSES}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={data?.map(({ id }) => id)}
                          allOptionValuesRef={dynamicAllValuesRef}
                          mode="multiple"
                          options={data?.map(({ id, name }) => ({
                            value: id,
                            label: name,
                          }))}
                          placeholder={FORM.CONDITIONS.PROCESSES}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: 0,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                      <Col span={24}>
                        <Row gutter={[16, 16]} align={"middle"}>
                          {get(
                            values,
                            `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                            [],
                          ).map((process, j) => (
                            <>
                              <Col span={24}>
                                <h3>
                                  {get(
                                    find(data, ({ id }) => id === process),
                                    "name",
                                  )}
                                </h3>
                              </Col>
                              <Col span={24}>
                                <Row gutter={[16, 16]}>
                                  <FieldArray
                                    key={`process-params${process}`}
                                    name={`params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`}
                                    render={(arrayHelpers) => [
                                      get(
                                        values,
                                        `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                        [],
                                      ).map((_, index) => (
                                        <>
                                          <Col span={7}></Col>
                                          <Col span={8}>
                                            <Select
                                              options={AVAILABLE_DOCUMENTS.map(
                                                (key) => ({
                                                  label:
                                                    DOCUMENT_NAME_LABEL[key],
                                                  value: key,
                                                }),
                                              )}
                                              placeholder={
                                                DETAIL.DOCUMENTS.PLACEHOLDER
                                              }
                                              {...bindArrayInputProps({
                                                parent: `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                index,
                                                name: `documentName`,
                                                values,
                                                ...formProps,
                                              })}
                                            />
                                          </Col>
                                          <Col span={8}>
                                            <Select
                                              options={Object.keys(
                                                DYNAMIC_DOCUMENTS[envelop]?.[
                                                  get(
                                                    values,
                                                    `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                  )
                                                ] || {},
                                              ).map((key) => ({
                                                label: get(
                                                  DOCUMENT_NAME,
                                                  `${envelop}.${get(
                                                    values,
                                                    `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                  )}.${key}`,
                                                ),
                                                value: key,
                                              }))}
                                              placeholder={
                                                DETAIL.DOCUMENTS.PLACEHOLDER
                                              }
                                              {...bindArrayInputProps({
                                                parent: `params.0.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                index,
                                                name: `documentVersion`,
                                                values,
                                                ...formProps,
                                              })}
                                            />
                                          </Col>
                                          <Col span={1}>
                                            <BiTrash
                                              color={COLORS.C_DANGER}
                                              size={24}
                                              onClick={() => {
                                                arrayHelpers.remove(index);
                                              }}
                                            />
                                          </Col>
                                        </>
                                      )),
                                      <Col key="add-document-row" span={24}>
                                        <Button
                                          type="primary"
                                          onClick={() => {
                                            arrayHelpers.push({
                                              process,
                                            });
                                          }}
                                        >
                                          <PlusOutlined
                                            color={COLORS.C_WHITE}
                                          />
                                        </Button>
                                      </Col>,
                                    ]}
                                  />
                                </Row>
                              </Col>
                            </>
                          ))}
                        </Row>
                      </Col>
                    </Row>
                  )}
                />
              </Col>
            </Row>
          </Card>
          <Card
            title={FORM.SUPPORTING_INFORMATION.TITLE}
            divider={false}
            key="additional_info"
          >
            <FieldArray
              name={"supportingInformation"}
              render={(arrayHelpers) => (
                <div className="grid--1">
                  {get(values, "supportingInformation", [])?.map((_, i) => (
                    <div className="d-flex" key={`additional_info-${i}`}>
                      <div className="flex--grow-1 grid--4">
                        <Select
                          options={SUPPORTING_INFORMATION_OPTIONS.map(
                            (value) => ({
                              value,
                              label: FORM.SUPPORTING_INFORMATION.OPTIONS[value],
                            }),
                          )}
                          label={FORM.SUPPORTING_INFORMATION.CATEGORY}
                          placeholder={FORM.SUPPORTING_INFORMATION.CATEGORY}
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index: i,
                            name: `category`,
                            values,
                            ...formProps,
                          })}
                        />
                        <Input
                          type="advanced"
                          label={FORM.SUPPORTING_INFORMATION.VALUE}
                          placeholder={FORM.SUPPORTING_INFORMATION.VALUE}
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index: i,
                            name: `value.mainInfo`,
                            values,
                            ...formProps,
                          })}
                        />
                        <Input
                          type="advanced"
                          label={FORM.SUPPORTING_INFORMATION.EXTRA_DETAIL}
                          placeholder={FORM.SUPPORTING_INFORMATION.EXTRA_DETAIL}
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index: i,
                            name: `value.extraDetail`,
                            values,
                            ...formProps,
                          })}
                        />
                        <Input
                          type="number"
                          label={FORM.SUPPORTING_INFORMATION.WEIGHT}
                          placeholder={FORM.SUPPORTING_INFORMATION.WEIGHT}
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index: i,
                            name: `weight`,
                            values,
                            ...formProps,
                          })}
                        />
                      </div>
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                      />
                    </div>
                  ))}

                  <Button
                    type="primary"
                    key="add-row"
                    onClick={() => {
                      arrayHelpers.push({ id: cuid(), category: null });
                    }}
                  >
                    <PlusOutlined color={COLORS.C_WHITE} />
                  </Button>
                </div>
              )}
            />
          </Card>
          <Upsells
            translation={FORM.UPSELLS}
            values={values}
            setFieldValue={setFieldValue}
            formProps={formProps}
          />
          <Documents
            values={values}
            setFieldValue={setFieldValue}
            formProps={formProps}
            translation={FORM}
            addAttachment={addAttachment}
            unlinkAttachmentFromInsurancePlan={
              unlinkAttachmentFromInsurancePlan
            }
          />
        </div>
      )}
    </Formik>
  );
};

export default View;
